import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  productImageSrcSet,
  productImageJpgFallback,
  productImageSrc,
  isDirectImagePath,
  PRODUCT_IMAGE_SIZES,
} from "@/lib/images";
import { ASSET } from "@/lib/api";

const LOAD_MARGIN = "500px";

const ProductImage = ({
  src,
  alt,
  className = "",
  priority = false,
  sizes = PRODUCT_IMAGE_SIZES,
  fill = true,
}) => {
  const hostRef = useRef(null);
  const imgRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(priority);
  const [loaded, setLoaded] = useState(false);

  const markLoaded = useCallback((img) => {
    if (img?.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  useEffect(() => {
    if (shouldLoad) markLoaded(imgRef.current);
  }, [shouldLoad, src, markLoaded]);

  useEffect(() => {
    if (priority) {
      setShouldLoad(true);
      return undefined;
    }

    setShouldLoad(false);
    const el = hostRef.current;
    if (!el) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: LOAD_MARGIN }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [src, priority]);

  if (!src) return null;

  const lqipSrc = !isDirectImagePath(src) ? productImageSrc(src, "xs") : null;
  const wrapperClass = fill ? (className || "w-full h-full") : "";
  const imgClass = fill
    ? `product-img absolute inset-0 w-full h-full object-cover${loaded ? " is-loaded" : ""}`
    : `block max-w-full opacity-0 transition-opacity duration-400 ease-out${loaded ? " opacity-100" : ""} ${className}`.trim();

  const imgProps = {
    alt,
    decoding: "async",
    fetchPriority: priority ? "high" : "auto",
    onLoad: () => setLoaded(true),
    ref: (el) => {
      imgRef.current = el;
      markLoaded(el);
    },
    className: imgClass,
  };

  const renderImage = () => {
    if (isDirectImagePath(src)) {
      return <img src={ASSET(src)} sizes={sizes} {...imgProps} />;
    }

    const webpSet = productImageSrcSet(src, "webp");

    if (!fill) {
      return (
        <picture>
          <source type="image/webp" srcSet={webpSet} sizes={sizes} />
          <img src={productImageJpgFallback(src)} {...imgProps} />
        </picture>
      );
    }

    return (
      <picture className="absolute inset-0 block w-full h-full">
        <source type="image/webp" srcSet={webpSet} sizes={sizes} />
        <img src={productImageJpgFallback(src)} {...imgProps} />
      </picture>
    );
  };

  return (
    <div
      ref={hostRef}
      className={fill ? `relative overflow-hidden ${wrapperClass}` : "inline-flex max-w-full"}
    >
      {lqipSrc && !loaded && fill && (
        <img
          src={lqipSrc}
          alt=""
          aria-hidden
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover scale-[1.02] blur-[3px] opacity-80"
        />
      )}
      {shouldLoad ? renderImage() : null}
    </div>
  );
};

export default React.memo(ProductImage);
